import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { getUnixTime, addSeconds, startOfDay, subMinutes, isSameDay, format, isAfter, fromUnixTime } from 'date-fns';
import { task, restartableTask, timeout } from 'ember-concurrency';
import { toLatLngObject } from 'garaje/helpers/resource-coords';
import type DeskModel from 'garaje/models/desk';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import type LocationsCapacity from 'garaje/models/locations-capacity';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type ReservationModel from 'garaje/models/reservation';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import type { ResourceState } from 'garaje/services/live-map';
import { AvailabilityStatus, ResourceType } from 'garaje/services/live-map';
import type LoggerService from 'garaje/services/logger';
import type MapSearchService from 'garaje/services/map-search';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { AssignmentSourceType } from 'garaje/types/maps/live-map/LiveMapServiceV2';
import { customPluralize } from 'garaje/utils/custom-pluralize';
import { SECONDS_IN_A_DAY } from 'garaje/utils/enums';
import { getPartialTimes, hourOptions } from 'garaje/utils/hour-options';
import { fetchCapacity } from 'garaje/utils/locations-capacity';
import urlBuilder from 'garaje/utils/url-builder';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { LatLngBounds, Map } from 'leaflet';
import leaflet from 'leaflet';
import { all } from 'rsvp';
import { cached } from 'tracked-toolbox';

import { type ModelResponse } from './route';

const SHARED_DESKS_ASSIGNMENT_SOURCE: AssignmentSourceType = 'SHARED_DESKS';
const NO_RESERVATIONS_DESK_FLAG = 'live-map-no-reservations-desk-web';

interface additionalDetailsResponse {
  reservations: ReservationModel[];
  assignedDesks: DeskModel[];
  availableDesks: DeskModel[] | undefined;
  selectedResourceFilter: string;
  selectedNeighborhoods: NeighborhoodModel[];
  isAfterBookingHours: boolean;
}

/**
 * Next desk assignment data from GraphQL search results
 * Defined locally until schema.graphql is updated with this type
 */
interface NextAssignment {
  deskId: string;
  deskName: string;
  startTime: string;
}

/**
 * Desk info structure used for employee selection
 * Can be populated from nextAssignment (GraphQL) or legacy logic
 */
interface DeskInfo {
  deskId: string;
  deskName: string;
  floorId: string;
  floorName: string;
  source: 'graphql' | 'legacy';
}

export default class MapsLiveShowController extends Controller {
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare liveMap: LiveMapService;
  @service declare mapSearch: MapSearchService;
  @service declare ajax: AjaxService;
  @service declare logger: LoggerService;

  declare model: ModelResponse;

  DEFAULT_RESOURCE_FILTER = 'all-resources';
  DESKS_FILTER = 'desks';

  get moveRequestsEnabled(): boolean {
    if (!this.featureFlags.isEnabled('moveRequests')) return false;
    const deskLocation = this.store.peekRecord('desk-location', this.state.currentLocation?.id);
    return !!deskLocation?.moveRequestsEnabled;
  }

  @tracked mapFeatures: MapFeatureModel[] = [];
  @tracked selectedFeature: Maybe<MapFeatureModel>;
  @tracked neighborhoods: NeighborhoodModel[] = [];
  @tracked selectedNeighborhoods: NeighborhoodModel[] = [];
  @tracked selectedTime: Maybe<number>;
  @tracked selectedEndTime: Maybe<number>;
  @tracked selectedTimeObject: { startTime: string; endTime: string } = this.getInitialSelectedTimeObject();
  @tracked selectedEmployee: Maybe<EmployeeModel>;
  @tracked selectedEmployeeId: Maybe<string>;
  @tracked selectedNextAssignment: Maybe<NextAssignment>;
  @tracked selectedResourceFilter = this.DEFAULT_RESOURCE_FILTER;
  @tracked employees: EmployeeModel[] = [];
  @tracked reservations: ReservationModel[] = [];
  @tracked availableDesks: DeskModel[] | undefined = undefined;
  @tracked leafletMap: Maybe<Map> = null;
  @tracked shareLiveMapModalOpen = false;
  @tracked groupDeskReservationCount: Maybe<number> = null;
  @tracked locationsCapacity: LocationsCapacity | undefined;

  // Map search
  @tracked searchTerm = '';
  @tracked showSearchResults = false;
  @tracked mapSearchNoResultsText = '';
  @tracked selectedFeatureId: string | undefined = undefined;

  // Toolbar scroll
  @tracked canScrollLeft = false;
  @tracked canScrollRight = false;
  toolbarScrollElement: HTMLElement | null = null;
  toolbarInnerElement: HTMLElement | null = null;
  toolbarScrollOffset = 0;
  _resizeObserver: ResizeObserver | null = null;

  // Search bar resize
  @tracked isSearchFocused = false;

  get isSearchExpanded(): boolean {
    return this.isSearchFocused || this.searchTerm.length > 0;
  }

  // Profile card
  @tracked profileCardOpen = false;
  @tracked openProfileModal: Maybe<string> = null;

  // Move requests
  @tracked moveRequestModalOpen = false;
  @tracked profileAssignedDeskIds: string[] = [];
  @tracked profileRequestDeskIds: string[] = [];
  @tracked profileCurrentDesk: { name: string; floorName: string | null; neighborhoodName: string | null } | null =
    null;
  @tracked profileHasDesks = false;

  // Map dimensions
  @tracked mapWidth: number | null = null;
  @tracked mapHeight: number | null = null;

  // SQ-7424: a feature that should be zoomed-to once ResourceMap signals its
  // base view is settled (i.e., after the 500ms image-cache fitBounds). Cleared
  // after the zoom fires or when the user clears the selection.
  pendingZoomFeature: MapFeatureModel | null = null;

  // Export
  @tracked exportModalOpen = false;

  queryParams = [
    // selectedTime refers to the start time of the reservation. If all-day booking toggle is enabled
    // then selectedTime will be set to the closest time interval to the current time. Otherwise, it
    // will be set to the start of the date selected in the calendar.
    'selectedTime',
    // selectedEndTime is only used when the all-day booking toggle is disabled.
    'selectedEndTime',
    'selectedResourceFilter',
    'selectedEmployeeId',
    'selectedFeatureId',
    'groupDeskReservationCount',
    'openProfileModal',
  ];

  get shouldShowClearButton(): boolean {
    return this.searchTerm.length > 0;
  }

  getInitialSelectedTimeObject(): { startTime: string; endTime: string } {
    const mbt = this.state.currentLocation ? this.state.minutesBetweenTimezones(new Date()) : 0;
    const locationNow = subMinutes(new Date(), mbt);
    const currentTime = format(locationNow, 'HH:mm:ss');
    const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
    const startTime = closestInterval?.value || '23:45:00';

    return {
      startTime,
      endTime: '23:59:00',
    };
  }

  get allMapFeatures(): MapFeatureModel[] {
    return this.store.peekAll('map-feature').slice();
  }

  get shouldShowMapSearchNoResults(): boolean {
    return this.mapSearchNoResultsText.length > 0 && this.searchTerm.length > 0;
  }

  private _buildDeskLookups(): {
    reservedDeskIdSet: Set<string>;
    desksById: Record<string, DeskModel | undefined>;
    availableDesksSet: Set<DeskModel>;
  } {
    const reservedDeskIdSet = new Set(this.reservationsOnCurrentFloor.map((res) => res.belongsTo('desk').id()));

    const desksById: Record<string, DeskModel | undefined> = Object.create(null) as Record<
      string,
      DeskModel | undefined
    >;
    for (const desk of this.model.desksInLocation) {
      desksById[desk.id] = desk;
    }

    const availableDesksSet = new Set(this.availableDesks);

    return { reservedDeskIdSet, desksById, availableDesksSet };
  }

  // Desk live states keyed by resource id, so the per-feature visibility checks can
  // look one up in O(1) instead of scanning the whole resourceStates array each time.
  // Tracks `liveMap.resourceStates`, so it rebuilds on every poll/filter refresh.
  @cached
  private get deskResourceStatesById(): Record<string, ResourceState | undefined> {
    const lookup: Record<string, ResourceState | undefined> = Object.create(null) as Record<string, ResourceState>;
    for (const state of this.liveMap.getResourceStatesByType(ResourceType.DESK)) {
      lookup[state.resourceId] = state;
    }
    return lookup;
  }

  // A desk blocked for reservations via a shared schedule must stay on the map for
  // wayfinding, but envoy-spaces excludes it from the static `desks` payload and
  // reports `enabled: false`, so the standard checks hide it. We keep it visible —
  // greyed out, with a "not open to reservations" popup — when its live ResourceState
  // matches the blocked-shared shape: a SHARED_DESKS source that is UNAVAILABLE with no
  // occupant. A SHARED_DESKS source alone is not enough — that source type also appears
  // on available/assigned shared desks, so we conjoin the blocked condition rather than
  // rely on the backend only ever emitting it in the blocked case. Truly disabled desks
  // have no such source. Gated behind the rollout flag, checked before touching the lookup.
  isNoReservationsSharedDesk(feature: MapFeatureModel, flagEnabled: boolean): boolean {
    if (!flagEnabled) {
      return false;
    }

    const state = this.deskResourceStatesById[feature.externalId];
    return (
      state?.assignmentSourceType === SHARED_DESKS_ASSIGNMENT_SOURCE &&
      state.status?.availabilityStatus === AvailabilityStatus.UNAVAILABLE &&
      !state.assignedTo
    );
  }

  get displayableMapFeatures(): MapFeatureModel[] {
    const desksIsEnabled = this.featureConfig.isEnabled('desks');
    const deskReservationsViewEnabled = this.featureConfig.isEnabled('deskReservations.view');
    const noReservationsFlagEnabled = this.featureFlags.isEnabled(NO_RESERVATIONS_DESK_FLAG);
    const { reservedDeskIdSet, desksById, availableDesksSet } = this._buildDeskLookups();

    return this.mapFeatures.filter((feature: MapFeatureModel) => {
      if (feature.type !== 'desk') {
        return feature.enabled;
      }

      if (!desksIsEnabled) {
        return false;
      }

      const featureHasReservation = reservedDeskIdSet.has(feature.externalId);

      const desk = desksById[feature.externalId];
      const featureHasAssignedDesk = desk?.assignedTo && !featureHasReservation;

      if (!deskReservationsViewEnabled && featureHasReservation && !featureHasAssignedDesk) {
        return false;
      }

      const featureHasAvailableDesk = !!desk && !!desk.enabled && availableDesksSet.has(desk);
      const result =
        featureHasAssignedDesk ||
        featureHasReservation ||
        desk?.enabled ||
        featureHasAvailableDesk ||
        this.isNoReservationsSharedDesk(feature, noReservationsFlagEnabled) ||
        false;
      return result;
    });
  }

  get placedResourceTypes(): string[] {
    let featureTypeList: string[] = [];

    this.displayableMapFeatures.forEach((feature) => {
      if (!featureTypeList.includes(customPluralize(feature.type))) {
        featureTypeList.push(customPluralize(feature.type));
      }
    });

    featureTypeList.sort();

    featureTypeList = featureTypeList.filter((type) => type !== this.DESKS_FILTER);

    if (this.featureConfig.isEnabled('deskReservations.booking') && this.featureConfig.isEnabled('desks')) {
      // Sort desks to the top of the list
      featureTypeList.unshift(this.DESKS_FILTER);
    }

    featureTypeList.unshift(this.DEFAULT_RESOURCE_FILTER);

    return featureTypeList;
  }

  get hasEmployee(): boolean {
    return !!this.currentAdmin.employee?.id;
  }

  // The default Intercom launcher is hidden on this route so it can't cover the
  // map zoom controls, so we surface a toolbar trigger instead. Mirror the rule
  // that governs the launcher elsewhere so chat isn't offered to users who
  // wouldn't otherwise see it.
  get showSupportChat(): boolean {
    const hiddenForNonAdmin =
      this.featureFlags.isEnabled('hide-intercom-launcher-for-non-admins') && !this.currentAdmin.isGlobalAdmin;
    return !hiddenForNonAdmin;
  }

  @action
  openSupportChat(): void {
    if (window.Intercom) {
      window.Intercom('show');
    }
  }

  @action
  trackAdminWithoutEmployee(): void {
    this.metrics.trackEvent('Admin without Employee attempted to access edit map', { user_id: this.currentAdmin.id });
  }

  get mapFeaturesToDisplay(): MapFeatureModel[] {
    const { desksById } = this._buildDeskLookups();
    const noReservationsFlagEnabled = this.featureFlags.isEnabled(NO_RESERVATIONS_DESK_FLAG);
    const neighborhoodsOnMapEnabled = this.featureFlags.isEnabled('neighborhoods-on-map-web');
    const noNeighborhoodFilter = isEmpty(this.selectedNeighborhoods);
    const selectedNeighborhoodNames = noNeighborhoodFilter
      ? null
      : new Set(this.selectedNeighborhoods.map((n) => n.name));
    const desksEnabled = this.featureConfig.isEnabled('desks');
    const isDesksOrDefaultFilter =
      this.selectedResourceFilter === this.DESKS_FILTER || this.selectedResourceFilter === this.DEFAULT_RESOURCE_FILTER;

    const shouldShowDesk = (feature: MapFeatureModel): boolean => {
      const desk = desksById[feature.externalId];
      const showAsNoReservations = this.isNoReservationsSharedDesk(feature, noReservationsFlagEnabled);

      const featureIsInNeighborhood =
        noNeighborhoodFilter ||
        neighborhoodsOnMapEnabled ||
        selectedNeighborhoodNames!.has(desk?.neighborhoodName || '');

      if (!desk) {
        return showAsNoReservations && featureIsInNeighborhood && isDesksOrDefaultFilter;
      }

      if (!this.hasEmployee && this.availableDesks === undefined) {
        return false;
      }

      const deskIsVisible = !!desk.enabled || !!desk.assignedTo || showAsNoReservations;

      return featureIsInNeighborhood && deskIsVisible && isDesksOrDefaultFilter;
    };

    if (this.selectedResourceFilter === this.DEFAULT_RESOURCE_FILTER) {
      return this.displayableMapFeatures.filter((feature) => {
        if (feature.type !== 'desk') {
          return true;
        }
        return shouldShowDesk(feature) && desksEnabled;
      });
    } else if (this.selectedResourceFilter === this.DESKS_FILTER) {
      return this.displayableMapFeatures.filter((feature) => {
        if (feature.type !== 'desk') {
          return false;
        }
        return shouldShowDesk(feature);
      });
    } else {
      return this.displayableMapFeatures.filter(
        (feature) => customPluralize(feature.type) === this.selectedResourceFilter,
      );
    }
  }

  get selectedDate(): Date {
    if (this.selectedTime) {
      const selectedDate = fromUnixTime(this.selectedTime);
      return this.state.getOfficeLocationTime(
        startOfDay(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate))),
      );
    }

    return this.state.getOfficeLocationTime(
      startOfDay(subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()))),
    );
  }

  get isAtCapacity(): boolean {
    const location = this.state.currentLocation;
    if (!location?.capacityLimitEnabled) return false;
    if (!this.locationsCapacity) return false;
    return !this.locationsCapacity.hasCapacity;
  }

  get selectedForToday(): boolean {
    const displayDate = subMinutes(this.selectedDate, this.state.minutesBetweenTimezones(this.selectedDate));
    const locationNow = subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()));
    return isSameDay(displayDate, locationNow);
  }

  get isAfterBookingHours(): boolean {
    if (!this.featureFlags.isEnabled('desks-out-working-hours')) {
      return false;
    }

    if (!this.selectedForToday) {
      return false;
    }

    const today = format(subMinutes(this.selectedDate, this.state.minutesBetweenTimezones(this.selectedDate)), 'EEEE');
    const currentWorkplaceDay = this.model.workplaceDays.find((day) => day.dayOfWeek === today);

    if (!currentWorkplaceDay) {
      return false;
    }

    // this.selectedDate is already startOfDay in the location's timezone, so
    // getPartialTimes gives the correct UTC timestamps for the location's working hours
    // no getOfficeLocationTime wrapper needed as partialEnd is already a correct UTC timestamp
    const [_, partialEnd] = getPartialTimes(this.selectedDate, {
      startTime: currentWorkplaceDay.startTime,
      endTime: currentWorkplaceDay.endTime,
    });

    if (!partialEnd) {
      return false;
    }

    return isAfter(new Date(), partialEnd);
  }

  get allDayEnabled(): boolean {
    return this.selectedEndTime == null;
  }

  get assignedDesks(): DeskModel[] {
    const reservedDeskIds = this.reservationsOnCurrentFloor.map((res) => {
      return res.belongsTo('desk').id();
    });

    return this.model.desksInLocation.filter((desk) => desk.assignedTo && !reservedDeskIds.includes(desk.id));
  }

  get additionalDetails(): additionalDetailsResponse {
    return {
      reservations: this.reservationsOnCurrentFloor,
      assignedDesks: this.assignedDesks,
      availableDesks: this.availableDesks,
      selectedResourceFilter: this.selectedResourceFilter,
      selectedNeighborhoods: this.selectedNeighborhoods,
      isAfterBookingHours: this.isAfterBookingHours,
    };
  }

  get sortedMapFloors(): MapFloorModel[] {
    if (!this.model?.mapFloors) {
      return [];
    }
    const mapFloors = [...this.model.mapFloors];
    return mapFloors.sort((a, b) => (a.ordinality || 0) - (b.ordinality || 0)).filter((floor) => floor.rasterImageUrl);
  }

  get searchedEmployees(): EmployeeModel[] {
    const employees = this.store
      .peekAll('employee')
      .slice()
      .filter((employee) => !employee.hideFromEmployeeSchedule);

    employees.sort((empA, empB) => {
      if (empA && empB) {
        return empA.name.localeCompare(empB.name);
      } else {
        return 0;
      }
    });

    return employees;
  }

  get reservationsOnCurrentFloor(): ReservationModel[] {
    const timeToCompare = this.selectedForToday ? new Date() : this.selectedDate;

    const desksWithReservations = this.model.desksInLocation.filter(
      (desk) => this.getAllFutureReservationsOnDesk(desk.id).length,
    );

    const resOnFloor: ReservationModel[] = [];

    desksWithReservations.forEach((desk) => {
      const allReservationsOnDesk = this.getAllFutureReservationsOnDesk(desk.id);
      let upcomingReservation;
      let timeUntilUpcomingReservation = Infinity;

      const currentRes = allReservationsOnDesk.find((reservation) => {
        const isCurrentReservation =
          reservation.startTime <= getUnixTime(timeToCompare) && reservation.endTime > getUnixTime(timeToCompare);

        if (isCurrentReservation) {
          return reservation;
        }

        const timeUntilReservation = reservation.startTime - getUnixTime(timeToCompare);

        if (timeUntilReservation > 0 && timeUntilReservation < timeUntilUpcomingReservation) {
          upcomingReservation = reservation;
          timeUntilUpcomingReservation = timeUntilReservation;
        }

        return false;
      });

      if (currentRes) {
        resOnFloor.push(currentRes);
      } else if (upcomingReservation) {
        resOnFloor.push(upcomingReservation);
      }
    });

    return resOnFloor;
  }

  get currentUserReservation(): ReservationModel | undefined {
    return this.reservations.find((reservation) => {
      if (reservation.userEmail !== this.state.currentUser?.email) return false;
      // When partial-day times are selected, only match reservations that overlap the selected window
      if (this.selectedTime != null && this.selectedEndTime != null) {
        return reservation.startTime < this.selectedEndTime && reservation.endTime > this.selectedTime;
      }
      return true;
    });
  }

  // this differs from above in that we are looking to find any reservations that belong to the current user within the resources states in the live-map
  get currentUserResourceReservations(): ResourceState[] {
    return this.liveMap.currentUserReservations;
  }

  getAllFutureReservationsOnDesk(deskId: string): ReservationModel[] {
    return this.reservations.filter((res) => {
      return res.belongsTo('desk').id() === deskId && res.endTime > getUnixTime(this.selectedDate);
    });
  }

  @action
  toggleShareLiveMapModal(): void {
    this.shareLiveMapModalOpen = !this.shareLiveMapModalOpen;
  }

  @action
  toggleExportModal(): void {
    this.exportModalOpen = !this.exportModalOpen;
  }

  @cached
  get exportMapBounds(): LatLngBounds | null {
    if (!this.mapWidth || !this.mapHeight) return null;
    return new leaflet.LatLngBounds(leaflet.latLng(0, 0), leaflet.latLng(-this.mapHeight, this.mapWidth));
  }

  get exportFileName(): string {
    const floorName = this.model.mapFloor?.name || 'map';
    const safe =
      floorName
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9._-]/g, '') // strip /, \, :, *, ?, ", <, >, |, control chars
        .replace(/^\.+/, '') // no leading dots (..)
        .toLowerCase() || 'map';
    return `${safe}-live-map`;
  }

  get exportMarkerNames(): Record<string, string> {
    const names: Record<string, string> = {};
    for (const feature of this.mapFeatures) {
      if (!feature.externalId) continue;
      const resourceType = feature.type === 'parking' ? ResourceType.PARKING : ResourceType.DESK;
      const resourceState = this.liveMap.getResourceStateByIdAndType(feature.externalId, resourceType);
      const name =
        resourceState?.status?.reservedForProfileData?.name || resourceState?.assignedToProfileData?.name || null;
      if (name) {
        names[feature.id] = name;
      }
    }
    return names;
  }

  @action
  onAvailablePopupCancel(map: Map): void {
    this.selectedFeature = null;
    map.closePopup();
  }

  @action
  onResourceFilterSelect(filter: string): void {
    this.clearSearchState();
    this.selectedResourceFilter = filter;
    this.selectedEmployee = null;

    if (!this.mapFeaturesToDisplay.length) {
      this.flashMessages.showAndHideFlash('warning', 'No desks available to book');
    }

    this.metrics.trackEvent('Element tapped', {
      element_identifier: 'map_filter',
      filter_type: filter,
      screen: 'map',
    });
  }

  @action
  onEmployeeSelect(employee: EmployeeModel, nextAssignment?: NextAssignment): void {
    this.clearSearchState();
    if (!employee) {
      this.selectedEmployee = null;
      this.selectedEmployeeId = null;
      this.selectedResourceFilter = this.DEFAULT_RESOURCE_FILTER;
      this.selectedFeature = null;
      // SQ-7424: setSelectedEmployee now populates selectedFeatureId on
      // cross-floor entry. Clear it here too so the toolbar × doesn't leave
      // a stale ?selectedFeatureId in the URL that would re-zoom on refresh.
      this.selectedFeatureId = undefined;
      this.pendingZoomFeature = null;
      return;
    }

    // Use new helper that prefers nextAssignment over legacy lookup
    const deskInfo = this.getEmployeeDeskInfo(employee, nextAssignment);

    this.selectedEmployee = employee;
    this.selectedEmployeeId = employee.id;
    this.selectedResourceFilter = this.DEFAULT_RESOURCE_FILTER;

    if (!deskInfo) {
      this.selectedFeature = null;
      return;
    }

    this.selectedFeature = this.displayableMapFeatures.find(
      (feature) => feature.type === 'desk' && feature.externalId === deskInfo.deskId,
    );

    const floorId = deskInfo.floorId;

    if (!floorId) {
      return;
    }

    // Only transition if we need to change floors
    if (floorId !== this.model?.mapFloor?.id) {
      this.router.transitionTo('spaces.maps.live.show', floorId, {
        queryParams: {
          selectedResourceFilter: this.DEFAULT_RESOURCE_FILTER,
          selectedTime: this.selectedTime,
          selectedEndTime: this.selectedEndTime,
          selectedEmployeeId: this.selectedEmployeeId,
        },
      });
    }
  }

  /**
   * Callback for when an employee is selected from search results.
   * Stores the nextAssignment for use in the desk popup.
   */
  @action
  onEmployeeSearchSelect(nextAssignment: NextAssignment | undefined): void {
    this.selectedNextAssignment = nextAssignment;
  }

  @action
  getEmployeeAttrById(userId: string, attr: string): string {
    const employee = this.employees.find((employee) => employee.belongsTo('user').id() == userId);
    return get(employee, attr) as string;
  }

  @action
  getEmployeeByUserEmail(userEmail: string): EmployeeModel | undefined {
    return this.employees.find((employee) => employee.email === userEmail);
  }

  @action
  getEmployeeByUserId(userId: string): EmployeeModel | undefined {
    return this.employees.find((employee) => employee.belongsTo('user').id() == userId);
  }

  getEmployeeDesk(employee: EmployeeModel): DeskModel | null {
    const assignedDesk = this.model.desksInLocation.find((desk) => desk.assignedTo === employee.email);

    if (assignedDesk) {
      return assignedDesk;
    }

    const reservationForEmployee = this.reservationsOnCurrentFloor.find(
      (res) => res.belongsTo('user').id() === employee.belongsTo('user').id(),
    );
    if (reservationForEmployee) {
      const deskId = reservationForEmployee.belongsTo('desk').id();
      if (deskId) {
        return this.store.peekRecord('desk', deskId);
      }
    }

    return null;
  }

  /**
   * Gets desk info for an employee, preferring nextAssignment if available.
   * Falls back to legacy logic for backward compatibility.
   *
   * @param employee - The employee model
   * @param nextAssignment - Optional nextAssignment from GraphQL search results
   * @returns DeskInfo or null if no desk found
   */
  getEmployeeDeskInfo(employee: EmployeeModel, nextAssignment?: NextAssignment): DeskInfo | null {
    // Use nextAssignment if available from GraphQL and has valid deskId
    if (nextAssignment?.deskId) {
      // Get floor info from the desk in the store (since GraphQL doesn't provide floorId/floorName yet)
      const desk = this.store.peekRecord('desk', nextAssignment.deskId);
      const floorId = desk?.belongsTo('floor').id() || '';
      const floor = floorId ? this.store.peekRecord('floor', floorId) : null;
      return {
        deskId: nextAssignment.deskId,
        deskName: nextAssignment.deskName,
        floorId,
        floorName: floor?.name || '',
        source: 'graphql',
      };
    }

    // Legacy fallback: search in local store
    const desk = this.getEmployeeDesk(employee);
    if (desk) {
      const floorId = desk.belongsTo('floor').id();
      const floor = floorId ? this.store.peekRecord('floor', floorId) : null;
      return {
        deskId: desk.id,
        deskName: desk.name,
        floorId: floorId || '',
        floorName: floor?.name || '',
        source: 'legacy',
      };
    }

    return null;
  }

  getWorkingHoursForDate(date: Date): { startTime: string; endTime: string } | null {
    const localDate = subMinutes(date, this.state.minutesBetweenTimezones(date));
    const dayOfWeek = format(localDate, 'EEEE');
    const activeDay = this.model.workplaceDays.find((d) => d.active && d.dayOfWeek === dayOfWeek);
    if (!activeDay) return null;

    let startTime = activeDay.startTime;
    // for today, don't pre-populate a time that's already in the past
    // use location-local time (not browser time) so the comparison is correct
    // when the user's timezone differs from the location's timezone
    let endTime = activeDay.endTime;

    const locationNow = subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()));
    if (isSameDay(localDate, locationNow)) {
      const currentTime = format(locationNow, 'HH:mm:ss');
      const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
      const nextTime = closestInterval?.value || '23:45:00';
      if (nextTime.localeCompare(activeDay.endTime) < 0) {
        // still within working hours: clamp start to next available interval
        startTime = nextTime.localeCompare(activeDay.startTime) >= 0 ? nextTime : activeDay.startTime;
      } else {
        // past working hours: use next interval as start and end of day as end
        startTime = nextTime;
        endTime = '23:59:00';
      }
    }

    return { startTime, endTime };
  }

  initializeSharedDesksState(): void {
    const workingHours = this.getWorkingHoursForDate(this.selectedDate);
    if (workingHours) {
      this.selectedTimeObject = workingHours;
    }
    this.updateQueryParamsForPartialTimes();
    void this.updateLiveMapDataWithPartialTimes();
  }

  @action
  onDateSelect(date: Date): void {
    this.clearSearchState();
    this.selectedFeature = null;

    // Always update the base selectedTime to the new date first
    this.selectedTime = getUnixTime(date);

    if (this.featureFlags.isEnabled('shared-desks-scheduling')) {
      const selectedDate = fromUnixTime(this.selectedTime);
      const workingHours = this.getWorkingHoursForDate(selectedDate);
      if (workingHours) {
        this.selectedTimeObject = workingHours;
      }
      this.updateQueryParamsForPartialTimes();
      void this.updateLiveMapDataWithPartialTimes();
    } else if (this.allDayEnabled) {
      void this.liveMap.getUpdatedLiveMapData({ selectedTime: this.selectedTime, floorId: this.model.mapFloor?.id });
    } else {
      // Partial day: preserve existing time range and apply it to the new date
      this.updateQueryParamsForPartialTimes();
      void this.updateLiveMapDataWithPartialTimes();
    }

    void this.loadResourcesDataTask.perform();
  }

  updateSelectedTimeObjectWithDefaults(): void {
    const isToday = this.selectedForToday;

    if (isToday) {
      // If it's today, use the next available time slot based on the location-local clock
      const locationNow = subMinutes(new Date(), this.state.minutesBetweenTimezones(new Date()));
      const currentTime = format(locationNow, 'HH:mm:ss');
      const closestInterval = hourOptions(15, true).find(({ value }) => value.localeCompare(currentTime) >= 0);
      const startTime = closestInterval?.value || '23:45:00';

      this.selectedTimeObject = {
        startTime,
        endTime: '23:59:00',
      };
    } else {
      // For other days, use the default start time
      this.selectedTimeObject = {
        startTime: '00:00:00',
        endTime: '23:59:00',
      };
    }
  }

  @action
  handleAllDayToggle(): void {
    const togglingToAllDay = !this.allDayEnabled;

    if (togglingToAllDay) {
      // Turning ON all-day: clear end time and anchor to the day
      this.selectedTime = getUnixTime(this.selectedDate);
      this.selectedEndTime = null;

      void this.liveMap.getUpdatedLiveMapData({
        selectedTime: this.selectedTime,
        floorId: this.model.mapFloor?.id,
      });
    } else {
      // Turning OFF all-day: initialize sensible defaults and set partial times
      this.updateSelectedTimeObjectWithDefaults();
      this.updateQueryParamsForPartialTimes();
      void this.updateLiveMapDataWithPartialTimes();
    }

    void this.loadResourcesDataTask.perform();
  }

  @action
  handlePartialTimeChange(key: 'startTime' | 'endTime', time: string): void {
    this.selectedTimeObject = {
      ...this.selectedTimeObject,
      [key]: time,
    };

    // Update query params to reflect the partial times
    this.updateQueryParamsForPartialTimes();

    // Update live map data with the new start/end times, following the same pattern as onDateSelect
    void this.updateLiveMapDataWithPartialTimes();
    void this.loadResourcesDataTask.perform();
  }

  updateQueryParamsForPartialTimes(): void {
    const { selectedDate } = this;
    const { startTime, endTime } = this.selectedTimeObject;

    // selectedDate is already startOfDay in the location's timezone
    const [partialStart, partialEnd] = getPartialTimes(selectedDate, { startTime, endTime });

    // Update query params: selectedTime = startTime, selectedEndTime = endTime
    this.selectedTime = getUnixTime(partialStart!);
    this.selectedEndTime = getUnixTime(partialEnd!);
  }

  updateLiveMapDataWithPartialTimes(): void {
    const { selectedDate } = this;
    const { startTime, endTime } = this.selectedTimeObject;

    // selectedDate is already startOfDay in the location's timezone
    const [partialStart, partialEnd] = getPartialTimes(selectedDate, { startTime, endTime });

    // Call live map service with explicit start/end times
    void this.liveMap.getUpdatedLiveMapData({
      selectedTime: getUnixTime(selectedDate),
      startTime: partialStart!.toISOString(),
      endTime: partialEnd!.toISOString(),
      floorId: this.model.mapFloor?.id,
    });
  }

  @action
  onMapFloorChange(mapFloor: FloorModel): void {
    this.clearSearchState();
    this.selectedFeature = null;
    this.selectedEmployee = null;

    this.router.transitionTo('spaces.maps.live.show', mapFloor.id, {
      queryParams: {
        selectedResourceFilter: this.selectedResourceFilter,
        selectedTime: this.selectedTime,
        selectedEndTime: this.selectedEndTime,
        selectedEmployeeId: null,
      },
    });
  }

  @action
  setSelectedEmployee(): void {
    if (this.selectedEmployeeId) {
      this.selectedEmployee = this.employees.find((employee) => employee.id === this.selectedEmployeeId);

      // Use new helper with legacy fallback (no nextAssignment available here)
      const deskInfo = this.getEmployeeDeskInfo(this.selectedEmployee as EmployeeModel);

      this.selectedFeature = this.displayableMapFeatures.find(
        (feature) => feature.type === 'desk' && feature.externalId === deskInfo?.deskId,
      );

      // Cross-floor search lands here without `selectedFeatureId` in the URL —
      // the source floor's mapFeatures cache can't resolve the target feature,
      // so the transition omits it. Set selectedFeatureId so clearSearchState
      // can tear down the selection, and stash the feature for handleMapReady
      // to zoom once ResourceMap's fitBounds has settled (otherwise the 500ms
      // image-cache fitBounds would override any zoom triggered now).
      if (this.selectedFeature != null && this.selectedFeatureId == null) {
        this.selectedFeatureId = this.selectedFeature.id;
        this.pendingZoomFeature = this.selectedFeature;
      }
    }
  }

  @action
  resetResourceFilter(): void {
    this.selectedResourceFilter = this.DEFAULT_RESOURCE_FILTER;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this.toolbarScrollElement?.removeEventListener('wheel', this.handleToolbarWheel);
    this.toolbarScrollElement?.removeEventListener('mousedown', this.handleToolbarItemMousedown, { capture: true });
    this.toolbarScrollElement = null;
    this.toolbarInnerElement = null;
    this.toolbarScrollOffset = 0;
  }

  @action
  setSelectedDesk(): void {
    if (this.selectedEmployee) {
      // Use new helper with legacy fallback (no nextAssignment available here)
      const deskInfo = this.getEmployeeDeskInfo(this.selectedEmployee);

      this.selectedFeature = this.displayableMapFeatures.find(
        (feature) => feature.type === 'desk' && feature.externalId === deskInfo?.deskId,
      );
    }
  }

  @action
  setEmployees(employee: EmployeeModel): void {
    const isEmployeeFetched = this.employees.find((fetchedEmployee) => fetchedEmployee.id === employee.id);

    if (!isEmployeeFetched) {
      this.employees.push(employee);
    }
  }

  @action
  searchNeighborhoods(searchTerm: string): NeighborhoodModel[] {
    return this.neighborhoods.filter((neighborhood) =>
      neighborhood.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  @action
  handleSelectNeighborhood(selected: NeighborhoodModel[]): void {
    this.selectedNeighborhoods = selected;
    this.clearSearchState();
  }

  loadAvailableDesksTask = task({ restartable: true }, async () => {
    const employeeById = this.getEmployeeByUserId(this.state.currentUser?.id || '');

    const currentEmployee =
      employeeById ??
      (() => {
        // (ar) this call relies on the DD integration, which needs to be debugged for the log statement to show up
        this.metrics.logMonitorError({
          event: 'COULD_NOT_FETCH_EMPLOYEE_BY_USER_ID',
          debugExtras: { user: this.state.currentUser },
        });
        return this.getEmployeeByUserEmail(this.state.currentUser?.email || '');
      })();

    if (currentEmployee) {
      // when partial-day scheduling is active use the selected time window so that
      // desks booked outside of that window are not incorrectly marked unavailable
      let availableFrom: number;
      let availableTo: number;
      if (!this.allDayEnabled) {
        const [partialStart, partialEnd] = getPartialTimes(this.selectedDate, this.selectedTimeObject);
        availableFrom = getUnixTime(partialStart!);
        availableTo = getUnixTime(partialEnd!);
      } else {
        availableFrom = getUnixTime(this.selectedDate);
        availableTo = getUnixTime(addSeconds(this.selectedDate, SECONDS_IN_A_DAY - 1));
      }

      const params = {
        filter: {
          'location-id': this.state.currentLocation?.id,
          'available-for': currentEmployee.email,
          'accessible-for': currentEmployee.email,
          'available-from': availableFrom,
          'available-to': availableTo,
        },
        include: 'amenities',
      };
      try {
        this.availableDesks = (await this.store.query('desk', params)).slice();
      } catch (e) {
        this.metrics.logMonitorError({
          event: 'MAPS_LOAD_AVAILABLE_DESK_QUERY_ERROR',
          debugExtras: { user: this.state.currentUser, employee: currentEmployee },
          error: e,
        });
        this.flashMessages.showAndHideFlash('warning', 'Unable to show available desks');
      }
    } else {
      this.metrics.logMonitorError({
        event: 'MAPS_LOAD_AVAILABLE_DESKS_NO_EMPLOYEE',
        debugExtras: { user: this.state.currentUser },
      });
      this.flashMessages.showAndHideFlash('warning', 'Unable to show available desks');
    }
  });

  loadEmployeesTask = task({ restartable: true }, async () => {
    const employeesPromises = [];
    let employeeResponses = [];
    const emailLimit = 50;
    const resUserEmails = this.reservationsOnCurrentFloor.map((res) => res.userEmail);
    const assignedToEmails = this.model.desksInLocation
      .filter((desk) => desk.assignedTo && !resUserEmails.includes(desk.assignedTo))
      .map((desk) => desk.assignedTo);
    const userEmails = [...resUserEmails, ...assignedToEmails, this.state.currentUser?.email];
    let offset = 0;
    let page = 0;
    while (offset < userEmails.length) {
      const userEmailsPaginated = userEmails.slice(offset, offset + emailLimit);
      const fetchedEmployeesPromise = this.store.query('employee', {
        filter: {
          locations: this.state.currentLocation?.id,
          'email-in': userEmailsPaginated.join(','),
          deleted: false,
        },
        fields: {
          employees: 'email,name,profile-picture-url,hide-from-employee-schedule,user',
          users: 'email,full-name,user-photo-url,hide-from-employee-schedule',
        },
        include: 'user',
        page: { limit: 100, offset: 0 },
      });
      page++;
      offset = page * emailLimit;
      employeesPromises.push(fetchedEmployeesPromise);
    }
    employeeResponses = await all(employeesPromises);
    const employees: EmployeeModel[] = [];
    employeeResponses.forEach((response) => response.forEach((employee) => employees.push(employee)));
    this.employees = employees;
  });

  loadNeighborhoodsTask = task({ restartable: true }, async () => {
    try {
      // Even though neighborhoods are loaded from the live-map endpoint,
      // there are additional attributes that aren't included in the response
      // that are needed for the UI (i.e. deskCountByFloor in the Neighborhood Popup)
      const result = await this.store.query('neighborhood', {
        filter: { 'location-id': this.state.currentLocation?.id },
        meta: 'desk-count,desk-count-by-floor',
      });
      this.neighborhoods = result.slice();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error({ e });
    }
  });

  loadReservationsTask = task({ restartable: true }, async () => {
    try {
      const filter = {
        'location-id': this.state.currentLocation?.id,
        'start-date': getUnixTime(this.selectedDate),
        'end-date': getUnixTime(addSeconds(this.selectedDate, SECONDS_IN_A_DAY - 1)),
      };
      const reservationParams = {
        filter,
        page: { limit: 999, offset: 0 },
      };
      const reservationResponse = await this.store.query('reservation', reservationParams);
      this.reservations = reservationResponse.slice();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error({ e });
    }
  });

  loadCapacityTask = task({ restartable: true }, async () => {
    const location = this.state.currentLocation;
    if (!location?.capacityLimitEnabled) {
      this.locationsCapacity = undefined;
      return;
    }
    this.locationsCapacity = await fetchCapacity(this.store, location, this.selectedDate);
  });

  loadResourcesDataTask = task({ restartable: true }, async () => {
    await Promise.all([
      this.loadCapacityTask.perform(),
      this.loadNeighborhoodsTask.perform(),
      this.store.query('delivery-area', { filter: { location: this.state.currentLocation?.id } }),
    ]);
    await this.loadReservationsTask.perform();
    await this.loadEmployeesTask.perform();
    await this.loadAvailableDesksTask.perform();
  });

  @action
  handleSearchInputChange(event: Event): void {
    if (!event || !event.target) {
      return;
    }

    if (this.mapSearchNoResultsText) {
      this.setMapSearchNoResultsText('');
    }

    this.selectedResourceFilter = this.DEFAULT_RESOURCE_FILTER;
    this.selectedFeatureId = undefined;
    this.selectedFeature = null;
    this.selectedEmployee = null;
    // Preserve selectedTime/selectedEndTime - search should work with selected date range

    const target = event.target as HTMLInputElement;
    const value = target.value || '';
    this.searchTerm = value;

    if (value.length > 0) {
      this.showSearchResults = true;
      void this.searchMapTask.perform(value);
    } else {
      // Only clear results when search term is empty
      this.showSearchResults = false;
      this.mapSearch.clearSearchResults();
    }
  }

  @action
  clearSearchState(): void {
    if (this.searchTerm.length > 0) {
      this.searchTerm = '';
      this.showSearchResults = false;
      this.mapSearch.clearSearchResults();

      if (this.selectedFeatureId != null) {
        this.selectedFeatureId = undefined;
        this.selectedFeature = null;
      }
      this.pendingZoomFeature = null;
      this.resetMapView();
    }
  }

  searchMapTask = restartableTask(async (query: string) => {
    await timeout(500); // Debounce

    // Use the map search service with selected time range if available
    await this.mapSearch.searchMapFeaturesAndEmployees.perform(query, {
      selectedTime: this.selectedTime ?? undefined,
      selectedEndTime: this.selectedEndTime ?? undefined,
    });
  });

  @action
  setSearchInput(value: string): void {
    this.searchTerm = value;
  }

  @action
  setMapSearchNoResultsText(value: string): void {
    this.mapSearchNoResultsText = value;
  }

  @action
  setSelectedFeatureFromQueryParam(): void {
    // Only run if we have mapFeatures loaded
    if (!Array.isArray(this.mapFeatures)) {
      return;
    }

    if (this.selectedFeatureId != null) {
      const found = this.mapFeatures.find((feature) => feature.id === this.selectedFeatureId);
      if (found) {
        this.selectedFeature = found;
        // SQ-7424: ResourceMap.onLoadImage schedules a fitBounds 500ms after
        // image load on every floor change that wipes the setView below.
        // Stash the feature so handleMapReady can re-apply the zoom once
        // fitBounds has settled. Harmless on same-floor (handleMapReady only
        // fires after image-load fitBounds, so the immediate setView wins and
        // the pending value lingers idle until cleared).
        this.pendingZoomFeature = found;
        this.centerMapOnFeature(found);
      }
    }
  }

  @action
  handleMapDimensionsLoaded({ mapWidth, mapHeight }: { mapWidth: number; mapHeight: number }): void {
    this.mapWidth = mapWidth;
    this.mapHeight = mapHeight;
  }

  @action
  toggleProfileCard(): void {
    this.profileCardOpen = !this.profileCardOpen;
  }

  /**
   * Deep-link handler: when the user arrives from an email CTA with
   * `?openProfileModal=true&selectedEmployeeId=…`, force-open the profile card
   * and clear the `openProfileModal` query param so refresh/back doesn't
   * re-trigger. `selectedEmployeeId` and `floor_id` are preserved. The QP is
   * cleared even when guards fail so a stale `?openProfileModal=true` can't
   * linger in the URL and re-fire on a later route entry.
   */
  @action
  maybeOpenProfileCardFromQueryParam(): void {
    if (this.openProfileModal !== 'true') {
      return;
    }

    if (this.selectedEmployeeId && this.moveRequestsEnabled) {
      this.profileCardOpen = true;
    }

    // Mirror the `groupDeskReservationCount` pattern used elsewhere in this
    // route: reset the tracked controller property synchronously AND issue a
    // transition so the URL is updated without waiting for a render to flush
    // the pending runloop work — if the guards skip `profileCardOpen = true`
    // there's no re-render to piggy-back on.
    this.openProfileModal = null;
    void this.router.transitionTo({ queryParams: { openProfileModal: null } });
  }

  @action
  closeProfileCard(): void {
    this.profileCardOpen = false;
  }

  // Move request actions

  get highlightedFeatureExternalIds(): string[] {
    if (this.profileCardOpen || this.moveRequestModalOpen) {
      return [...this.profileAssignedDeskIds, ...this.profileRequestDeskIds];
    }
    return [];
  }

  @action
  openMoveRequestModal(): void {
    this.profileCardOpen = false;
    this.moveRequestModalOpen = true;
  }

  @action
  closeMoveRequestModal(): void {
    this.moveRequestModalOpen = false;
  }

  @action
  handleMoveRequestSubmitted(): void {
    this.moveRequestModalOpen = false;
    this.flashMessages.showAndHideFlash('success', 'Move request submitted successfully.');
  }

  @action
  handleProfileLoaded(data: {
    assignedDesks: {
      clientId: string;
      name: string;
      floorName: string | null;
      locationId: number;
      neighborhoodName: string | null;
    }[];
  }): void {
    const desks = data.assignedDesks ?? [];
    this.profileAssignedDeskIds = desks.map((d) => d.clientId);
    const currentLocationId = Number(this.state.currentLocation.id);
    const currentLocationDesks = desks.filter((d) => d.locationId === currentLocationId);
    this.profileHasDesks = currentLocationDesks.length > 0;
    if (currentLocationDesks.length > 0) {
      const first = currentLocationDesks[0]!;
      this.profileCurrentDesk = {
        name: first.name,
        floorName: first.floorName,
        neighborhoodName: first.neighborhoodName,
      };
    } else {
      this.profileCurrentDesk = null;
    }
  }

  @action
  async handleCancelRequest(moveRequestId: string): Promise<void> {
    this.profileCardOpen = false;
    try {
      await this.ajax.request(urlBuilder.moveRequests.cancelUrl(moveRequestId), {
        type: 'DELETE',
      });
      this.flashMessages.showAndHideFlash('success', 'Move request cancelled.');
    } catch (error) {
      this.logger.error(error instanceof Error ? error : new Error('Failed to cancel move request'));
      this.flashMessages.showAndHideFlash('error', 'Unable to cancel move request. Please try again.');
    }
  }

  @action
  centerMapOnFeature(feature: MapFeatureModel): void {
    if (this.leafletMap == null || this.mapWidth == null || this.mapHeight == null) {
      return;
    }

    const [posX, posY] = feature.geometry.coordinates;

    const latLng = toLatLngObject([posX, posY], [this.mapWidth, this.mapHeight]);
    this.leafletMap.setView(latLng, this.leafletMap.getZoom() + 1);
  }

  // SQ-7424: ResourceMap calls this each time the base view settles after a
  // floor-image load. If a cross-floor selection stashed a feature for
  // deferred zoom, apply it now via the same code path that same-floor uses
  // — guarded against stale selection so a floor change or clear between
  // stash and ready doesn't zoom to the wrong desk.
  @action
  handleMapReady(): void {
    const pending = this.pendingZoomFeature;
    if (pending == null) return;
    this.pendingZoomFeature = null;
    if (this.selectedFeatureId === pending.id || this.selectedFeature === pending) {
      this.centerMapOnFeature(pending);
    }
  }

  @action
  resetMapView(): void {
    const { mapWidth, mapHeight } = this;
    const mapBounds = new leaflet.LatLngBounds(
      // @ts-expect-error
      leaflet.CRS.Simple.unproject({ x: 0, y: 0 }, 0),
      // @ts-expect-error
      leaflet.CRS.Simple.unproject({ x: mapWidth, y: -mapHeight! }, 0),
    );

    this.leafletMap?.fitBounds(mapBounds, { padding: [1, 1] });
  }

  // --- Toolbar scroll ---

  @action
  setupToolbarScroll(element: HTMLElement): void {
    this.toolbarScrollElement = element;
    this.toolbarInnerElement = element.querySelector('[data-test-toolbar-scroll-inner]') as HTMLElement;
    this.toolbarScrollOffset = 0;
    this.updateScrollArrows();

    this._resizeObserver = new ResizeObserver(() => {
      this.clampScrollOffset();
      this.applyScrollTransform();
      this.updateScrollArrows();
    });
    this._resizeObserver.observe(element);

    element.addEventListener('wheel', this.handleToolbarWheel, { passive: false });
    element.addEventListener('mousedown', this.handleToolbarItemMousedown, { capture: true });
  }

  get maxScrollOffset(): number {
    const outer = this.toolbarScrollElement;
    const inner = this.toolbarInnerElement;
    if (!outer || !inner) return 0;
    return Math.max(0, inner.offsetWidth - outer.offsetWidth);
  }

  clampScrollOffset(): void {
    this.toolbarScrollOffset = Math.max(0, Math.min(this.toolbarScrollOffset, this.maxScrollOffset));
  }

  applyScrollTransform(): void {
    if (this.toolbarInnerElement) {
      this.toolbarInnerElement.style.transform = `translateX(-${this.toolbarScrollOffset}px)`;
    }
  }

  updateScrollArrows(): void {
    const threshold = 2;
    this.canScrollLeft = this.toolbarScrollOffset > threshold;
    this.canScrollRight = this.toolbarScrollOffset < this.maxScrollOffset - threshold;
  }

  scrollChildIntoView(target: HTMLElement): boolean {
    const inner = this.toolbarInnerElement;
    const outer = this.toolbarScrollElement;
    if (!inner || !outer || this.maxScrollOffset <= 0) return false;

    // Find the direct child of the inner container that contains the target element
    const child = Array.from(inner.children).find((el) => el.contains(target)) as HTMLElement | undefined;
    if (!child) return false;

    // Account for scroll arrow button width (w-11 = 44px) so items sit beside the arrows
    const arrowWidth = 44;
    const childLeft = child.offsetLeft;
    const childRight = childLeft + child.offsetWidth;
    const visibleLeft = this.toolbarScrollOffset + (this.canScrollLeft ? arrowWidth : 0);
    const visibleRight = this.toolbarScrollOffset + outer.offsetWidth - (this.canScrollRight ? arrowWidth : 0);

    if (childLeft < visibleLeft) {
      // Element is clipped or behind the left arrow
      this.toolbarScrollOffset = Math.max(0, childLeft - arrowWidth);
    } else if (childRight > visibleRight) {
      // Element is clipped or behind the right arrow
      this.toolbarScrollOffset = childRight - outer.offsetWidth + arrowWidth;
    } else {
      return false; // Already fully visible
    }

    this.clampScrollOffset();
    this.applyScrollTransform();
    this.updateScrollArrows();
    return true;
  }

  handleToolbarItemMousedown = (event: MouseEvent): void => {
    const scrolled = this.scrollChildIntoView(event.target as HTMLElement);
    if (scrolled) {
      // Skip the CSS transition so the item is already in its final position
      // by the time the click reaches the dropdown trigger. This ensures
      // dropdowns open at the correct location.
      const inner = this.toolbarInnerElement;
      if (inner) {
        inner.style.transition = 'none';
        // Force a layout so the transform applies immediately
        void inner.offsetHeight;
        inner.style.transition = '';
      }
    }
  };

  handleToolbarWheel = (event: WheelEvent): void => {
    if (this.maxScrollOffset <= 0) return;

    // Walk up from the event target to see if it's inside a vertically scrollable
    // child (e.g. the search results dropdown). If so, let the browser handle the
    // scroll natively instead of converting it into horizontal toolbar movement.
    let el = event.target as HTMLElement | null;
    while (el && el !== this.toolbarScrollElement) {
      if (el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'visible') return;
      el = el.parentElement;
    }

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;
    event.preventDefault();
    this.toolbarScrollOffset = Math.max(0, Math.min(this.toolbarScrollOffset + delta, this.maxScrollOffset));
    this.applyScrollTransform();
    this.updateScrollArrows();
  };

  @action
  scrollToolbarLeft(): void {
    const inner = this.toolbarInnerElement;
    const outer = this.toolbarScrollElement;
    if (!inner || !outer) return;

    const arrowWidth = 44;
    const visibleLeft = this.toolbarScrollOffset;

    // Find the rightmost child whose left edge is clipped or behind the left arrow
    let target: HTMLElement | null = null;
    for (const el of Array.from(inner.children) as HTMLElement[]) {
      if (el.offsetLeft < visibleLeft + arrowWidth) {
        target = el;
      }
    }

    if (target) {
      this.toolbarScrollOffset = Math.max(0, target.offsetLeft - arrowWidth);
    } else {
      this.toolbarScrollOffset = 0;
    }

    this.clampScrollOffset();
    this.applyScrollTransform();
    this.updateScrollArrows();
  }

  @action
  scrollToolbarRight(): void {
    const inner = this.toolbarInnerElement;
    const outer = this.toolbarScrollElement;
    if (!inner || !outer) return;

    const arrowWidth = 44;
    const visibleRight = this.toolbarScrollOffset + outer.offsetWidth;

    // Find the first child whose right edge extends past the visible area (accounting for arrow)
    for (const el of Array.from(inner.children) as HTMLElement[]) {
      const childRight = el.offsetLeft + el.offsetWidth;
      if (childRight > visibleRight - arrowWidth) {
        this.toolbarScrollOffset = childRight - outer.offsetWidth + arrowWidth;
        this.clampScrollOffset();
        this.applyScrollTransform();
        this.updateScrollArrows();
        return;
      }
    }
  }

  // --- Search bar resize ---

  @action
  handleSearchFocus(): void {
    this.isSearchFocused = true;
  }

  @action
  handleSearchBlur(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    schedule('afterRender', this, () => {
      this.isSearchFocused = false;
    });
  }
}
